import React, {useEffect, useRef} from 'react';
import {InputMain, NumericFormatCustom, PhoneInput2, SelectMain, SelectTimeOnly, TextToolTip} from "components";
import {Settings, EscortAppPatientEnum, EscortAppPatientList, LegTypesEnum, EscortAppPatientStatuses} from "fragments";
import {Notes} from "./notes";
import {convertTo12HourFormat, dontAllowChangesLegStatuses, usePrimaryModal} from "utils";
import {Svg} from "assets";
import {legsService} from "../../../store/legs/legs.service";

export const SingleLeg = (
    {
        currentTripId,
        currentItem,
        singleTrip,
        isEditTrip,
        vehiclesList,
        changeAddress,
        control,
        watch,
        errors,
        index,
        settingsListLeg,
        setValue
    }) => {
    const {openPrimary} = usePrimaryModal();
    const forceCalcRef = useRef(false);

    const vehicle = watch(`legs.${[index]}.vehicle`);
    const currentLeg = watch(`legs.${[index]}`);
    const mainLeg = singleTrip?.legs?.[index];
    const status = singleTrip?.legs?.[index]?.status;
    const currentStatus = watch(`legs.${[index]}.status`);

    const origin = singleTrip?.legs?.[index]?.pickUp?.address;
    const destination = singleTrip?.legs?.[index]?.dropOff?.address;

    const currentOrigin = watch(`legs.${[index]}.pickupAddress`);
    const currentDestination = watch(`legs.${[index]}.dropOffAddress`)

    const currentOriginFacility = watch(`legs.${[index]}.pickupFacility`);
    const currentDestinationFacility = watch(`legs.${[index]}.dropOffFacility`);

    const dontAllowChangesStatuses = currentTripId && dontAllowChangesLegStatuses(status)
    const dontAllowChangesLogsStatuses = dontAllowChangesLegStatuses(currentStatus) || currentStatus === EscortAppPatientEnum.OPEN
    const dontAllowChangesLogs = dontAllowChangesLogsStatuses || !vehicle

    const CurrentStatusSvg = EscortAppPatientList?.find((esc) => esc.id === currentStatus)?.Icon

    useEffect(() => {
        if (vehicle) {
            if ((!dontAllowChangesLegStatuses(status))) {
                if (!watch(`legs.${[index]}.arrivedTime`) && !watch(`legs.${[index]}.pickupTime`) && !watch(`legs.${[index]}.dropOffTime`)) {
                    setValue(`legs.${[index]}.status`, EscortAppPatientEnum.ASSIGNED);
                } else if (watch(`legs.${[index]}.arrivedTime`) && !watch(`legs.${[index]}.pickupTime`) && !watch(`legs.${[index]}.dropOffTime`)) {
                    setValue(`legs.${[index]}.status`, EscortAppPatientEnum.ARRIVED);
                } else if (watch(`legs.${[index]}.arrivedTime`) && watch(`legs.${[index]}.pickupTime`) && !watch(`legs.${[index]}.dropOffTime`)) {
                    setValue(`legs.${[index]}.status`, EscortAppPatientEnum.EN_ROUT);
                } else if (watch(`legs.${[index]}.arrivedTime`) && watch(`legs.${[index]}.pickupTime`) && watch(`legs.${[index]}.dropOffTime`)) {
                    setValue(`legs.${[index]}.status`, EscortAppPatientEnum.COMPLETED);
                } else {
                    setValue(`legs.${[index]}.status`, EscortAppPatientEnum.OPEN);
                }
            }
        } else {
            if ((!dontAllowChangesLegStatuses(status))) {
                setValue(`legs.${[index]}.status`, EscortAppPatientEnum.OPEN)
            }
        }

        if (!watch(`legs.${[index]}.pickupTime`)) {
            setValue(`legs.${[index]}.pickupMileage`, 0);
        }

        if (!watch(`legs.${[index]}.dropOffTime`)) {
            setValue(`legs.${[index]}.dropOffMileage`, 0);
        }

    }, [watch(`legs.${[index]}.arrivedTime`), watch(`legs.${[index]}.pickupTime`), watch(`legs.${[index]}.dropOffTime`), vehicle, status]);


    useEffect(() => {
        let cancelled = false;

        const originFormatted = origin?.formattedAddress;
        const destinationFormatted = destination?.formattedAddress;
        const currentOriginFormatted = currentOrigin?.formattedAddress;
        const currentDestinationFormatted = currentDestination?.formattedAddress;

        const hasBothCurrent = currentOriginFormatted && currentDestinationFormatted;

        const isOriginSame = currentOriginFormatted && originFormatted === currentOriginFormatted;
        const isDestinationSame = currentDestinationFormatted && destinationFormatted === currentDestinationFormatted;

        if (!hasBothCurrent) {
            // No calc possible yet — clear the swap flag so it can't leak into a later, unrelated run.
            forceCalcRef.current = false;
            return;
        }

        const forced = forceCalcRef.current;
        forceCalcRef.current = false;

        if (isOriginSame && isDestinationSame && !forced) {
            setValue(`legs.${index}.distance`, mainLeg?.distance);
        } else {
            (async () => {
                try {
                    const body = {
                        origin: currentOrigin,
                        destination: currentDestination,
                    };
                    const { data } = await legsService.calculateDistance(body);
                    if (!cancelled && watch(`legs.${index}.type`)) {
                        setValue(`legs.${index}.distance`, data);
                    }
                } catch {
                    // Distance service failed — keep the previous distance value.
                }
            })();
        }

        return () => { cancelled = true; };
    }, [
        origin?.formattedAddress,
        destination?.formattedAddress,
        currentOrigin?.formattedAddress,
        currentDestination?.formattedAddress,
        index,
        mainLeg?.distance,
    ]);


    const overwriteAddresses = () => {
        if ((currentTripId && isEditTrip) || !currentTripId) {
            forceCalcRef.current = true;
            setValue(`legs.${[index]}.pickupAddress`, currentDestination);
            setValue(`legs.${[index]}.dropOffAddress`, currentOrigin);

            setValue(`legs.${[index]}.pickupFacility`, currentDestinationFacility);
            setValue(`legs.${[index]}.dropOffFacility`, currentOriginFacility);
        }
    }

    const handleAddNote = () => {
        openPrimary(
            <Notes
                tripId={currentTripId}
                legId={currentLeg?.id}
            />,
            {
                noShowCloseBtn: true,
                padding: "noPaddings",
            },
        )
    }

    return (
        <div className={`escort-appointment-patient-wrapper
         ${(currentLeg?.type === LegTypesEnum.ESCORT_PICKUP
            || currentLeg?.type === LegTypesEnum.MED_DROP
            || currentLeg?.type === LegTypesEnum.BUS_ROUTE
        )
            ?
            'escort-type-escort-pickup'
            :
            (currentLeg?.type === LegTypesEnum.APPOINTMENT
                || currentLeg?.type === LegTypesEnum.MED_DROP_RETURN
                || currentLeg?.type === LegTypesEnum.BUS_ROUTE_RETURN)
                ?
                'escort-type-appointment'
                :
                currentLeg?.type === LegTypesEnum.RETURN ? 'escort-type-return' : 'escort-type-escort-return'}`}
        >
            <div className="escort-appointment-patient-box">
                <div className='escort-appointment-patient-header'>
                    <div
                        className="escort-appointment-patient-header-block"
                        style={{gap: currentTripId ? 8 : ''}}
                    >
                        <div
                            className='flex-all-center'
                            style={{
                                width: '100%',
                                gap: 12,
                                justifyContent: 'space-between',
                                pointerEvents: dontAllowChangesStatuses ? 'none' : 'auto',
                                opacity: dontAllowChangesStatuses ? '.6' : '1',
                            }}
                        >
                            <div className='flex-gap-12'>
                                <div
                                    className="escort-appointment-patient-svg"
                                    style={{background: currentItem?.iconBackground}}
                                >
                                    {currentItem?.Icon}
                                </div>

                                <div className="escort-appointment-patient-title">
                                    {currentItem?.title}
                                </div>
                            </div>

                            <div className='flex-gap-12' style={{justifyContent: 'flex-end'}}>
                                {(currentLeg?.type === LegTypesEnum.APPOINTMENT
                                        || currentLeg?.type === LegTypesEnum.RETURN
                                        || currentLeg?.type === LegTypesEnum.MED_DROP
                                        || currentLeg?.type === LegTypesEnum.MED_DROP_RETURN
                                        || currentLeg?.type === LegTypesEnum.BUS_ROUTE
                                        || currentLeg?.type === LegTypesEnum.BUS_ROUTE_RETURN
                                    ) &&
                                    <div className="escort-appointment-patient-percentage-wrapper">
                                        <Svg.PercentageSvg/>
                                        <p className='escort-appointment-patient-percentage-title'>
                                            Call
                                        </p>

                                        <TextToolTip
                                            classNameText='escort-appointment-patient-percentage-text'
                                            content={watch(`legs.${[index]}.callRevenue`)}
                                            text={watch(`legs.${[index]}.callRevenue`)}
                                            priceRow
                                        />
                                    </div>
                                }

                                <div className="escort-appointment-patient-percentage-wrapper">
                                    <Svg.PercentageSvg/>
                                    <p className='escort-appointment-patient-percentage-title'>
                                        Mi. Rate
                                    </p>

                                    <TextToolTip
                                        classNameText='escort-appointment-patient-percentage-text'
                                        content={watch(`legs.${[index]}.revenue`)}
                                        text={watch(`legs.${[index]}.revenue`)}
                                        priceRow
                                    />
                                </div>
                                {(currentLeg?.id) &&
                                    <div style={{width: '100%', maxWidth: 'fit-content'}}>
                                        <div className="escort-appointment-patient-percentage-wrapper">
                                            {CurrentStatusSvg &&
                                                <CurrentStatusSvg
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        minWidth: 10,
                                                        minHeight: 10,
                                                        marginTop: 2
                                                    }}
                                                />
                                            }

                                            <p className='escort-appointment-patient-percentage-text'>
                                                {EscortAppPatientStatuses?.[currentStatus]}
                                            </p>
                                        </div>

                                        {/*<SelectMain*/}
                                        {/*    list={*/}
                                        {/*        status === EscortAppPatientEnum.CANCELLED ? EscortAppPatientList.filter((item) => item.id === EscortAppPatientEnum.CANCELLED)*/}
                                        {/*            :*/}
                                        {/*            vehicle ?*/}
                                        {/*                EscortAppPatientList.filter((item) =>*/}
                                        {/*                    (item.id !== EscortAppPatientEnum.OPEN && item.id !== EscortAppPatientEnum.CANCELLED))*/}

                                        {/*                : !vehicle ? EscortAppPatientList.filter((item) =>*/}
                                        {/*                        (item.id !== EscortAppPatientEnum.ASSIGNED && item.id !== EscortAppPatientEnum.CANCELLED))*/}
                                        {/*                    : EscortAppPatientList.filter((item) => item.id !== EscortAppPatientEnum.CANCELLED)*/}
                                        {/*    }*/}
                                        {/*    renderLabel={(option) => option?.name}*/}
                                        {/*    name={`legs.${[index]}.status`}*/}
                                        {/*    control={control}*/}
                                        {/*    placeholder="Select"*/}
                                        {/*    disableClearable*/}
                                        {/*    selectedValue={'id'}*/}
                                        {/*    disabled={currentTripId && tripId && !isEditTrip}*/}
                                        {/*    allowError={false}*/}
                                        {/*    rules={{*/}
                                        {/*        required: true*/}
                                        {/*    }}*/}
                                        {/*/>*/}
                                    </div>
                                }
                            </div>
                        </div>

                        {(currentLeg?.id) &&
                            <div className='line'/>
                        }

                        {(currentLeg?.id) &&
                            <button
                                className='reset-btn'
                                type='button'
                                onClick={handleAddNote}
                            >
                                <Svg.Note3Svg/>
                            </button>
                        }

                        {(currentLeg?.id) &&
                            <Settings
                                className='settings-leg-trip'
                                list={settingsListLeg}
                                currentITem={currentLeg}
                                mainLeg={mainLeg}
                            />
                        }
                    </div>

                    <div
                        className='escort-appointment-patient-header-block'
                    >
                        <div className='flex-gap-12' style={{justifyContent: 'flex-end'}}>
                            <div className='dispatch-board-input-wrapper'
                                 style={{
                                     justifyContent: 'flex-end',
                                     pointerEvents: (currentTripId && status === EscortAppPatientEnum.CANCELLED) ? 'none' : 'auto',
                                     opacity: (currentTripId && status === EscortAppPatientEnum.CANCELLED) ? '.6' : '1',
                                 }}>
                                <p className='dispatch-board-input-label'>
                                    VH
                                </p>

                                <SelectMain
                                    list={vehiclesList?.vehicles}
                                    renderLabel={(option) => option.unitNumber}
                                    name={`legs.${[index]}.vehicle`}
                                    control={control}
                                    placeholder="Select Vehicles"
                                    loadType="GET_VEHICLES"
                                    selectedValue={'id'}
                                    rules={{
                                        required: (currentStatus && (currentStatus !== EscortAppPatientEnum.OPEN && !dontAllowChangesLegStatuses(currentStatus)))
                                    }}
                                    disabled={currentTripId && !isEditTrip}
                                    allowError={false}
                                    disableClearable={status !== EscortAppPatientEnum.NO_SHOW && status !== EscortAppPatientEnum.OPEN && status !== EscortAppPatientEnum.ASSIGNED && currentLeg?.id}
                                />
                            </div>

                            <div className='dispatch-board-input-wrapper'
                                 style={{
                                     justifyContent: 'flex-end',
                                     pointerEvents: (dontAllowChangesStatuses || (currentTripId && !isEditTrip)) ? 'none' : 'auto',
                                     opacity: (dontAllowChangesStatuses || (currentTripId && !isEditTrip)) ? '.6' : '1',
                                 }}>

                                <p className='dispatch-board-input-label'>
                                    PH
                                </p>

                                <PhoneInput2
                                    placeholder={"ex (000) 000-0000"}
                                    name={`legs.${[index]}.phone`}
                                    control={control}
                                    rules={{
                                        minLength: {
                                            value: 10,
                                            message: "Please enter a valid 10 digit number.",
                                        },
                                        validate: (value) => {
                                            return value
                                                ? value?.replace(/[^0-9]/g, "").length === 10 ||
                                                "Please enter a valid phone number."
                                                : true;
                                        },
                                    }}
                                    allowError={false}
                                    disabled={currentTripId && !isEditTrip}
                                />
                            </div>

                            <div className='dispatch-board-input-wrapper'
                                 style={{
                                     justifyContent: 'flex-end',
                                     pointerEvents: dontAllowChangesStatuses ? 'none' : 'auto',
                                     opacity: dontAllowChangesStatuses ? '.6' : '1',
                                 }}
                            >
                                <p className='dispatch-board-input-label'>
                                    P/U
                                </p>

                                <SelectTimeOnly
                                    name={`legs.${[index]}.scheduledTime`}
                                    control={control}
                                    placeholder="00:00 AM"
                                    dontShowError
                                    disabled={currentTripId && !isEditTrip}
                                    rules={{
                                        required: true
                                    }}
                                />
                            </div>

                            {(currentLeg?.type === LegTypesEnum.APPOINTMENT
                                    || currentLeg?.type === LegTypesEnum.MED_DROP
                                    || currentLeg?.type === LegTypesEnum.BUS_ROUTE
                                )
                                &&
                                <div
                                    className='dispatch-board-input-wrapper'
                                    style={{
                                        justifyContent: 'flex-end',
                                        pointerEvents: dontAllowChangesStatuses ? 'none' : 'auto',
                                        opacity: dontAllowChangesStatuses ? '.6' : '1',
                                    }}
                                >
                                    <p className='dispatch-board-input-label'>
                                        APPT
                                    </p>

                                    <SelectTimeOnly
                                        name={`legs.${[index]}.apptTime`}
                                        control={control}
                                        placeholder="00:00 AM"
                                        dontShowError
                                        disabled={currentTripId && !isEditTrip}
                                        rules={{
                                            required: true
                                        }}
                                    />
                                </div>
                            }


                            <div
                                className='dispatch-board-input-wrapper'
                                style={{
                                    justifyContent: 'flex-end',
                                    pointerEvents: dontAllowChangesStatuses ? 'none' : 'auto',
                                    opacity: dontAllowChangesStatuses ? '.6' : '1',
                                }}
                            >
                                <p className='dispatch-board-input-label'>
                                    Dist.
                                </p>
                                <NumericFormatCustom
                                    name={`legs.${[index]}.distance`}
                                    placeholder="0,000.00mi"
                                    thousandSeparator={true}
                                    fixedDecimalScale={true}
                                    decimalScale={2}
                                    control={control}
                                    disabled={!isEditTrip || !currentLeg?.id}
                                    showError={false}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className='escort-appointment-patient-main'
                     style={
                         dontAllowChangesStatuses ? {
                             pointerEvents: 'none',
                             opacity: '.6'
                         } : {}}
                >
                    <div
                        className='escort-appointment-patient-main_info-wrapper'
                        style={{
                            maxWidth: 150,
                            pointerEvents: dontAllowChangesLogs ? 'none' : 'auto',
                            opacity: dontAllowChangesLogs ? '.6' : '1'
                        }}
                    >
                        <div className='escort-appointment-patient-main_info-header'>
                            <p className='escort-appointment-patient-main_info-title'>
                                Arrive
                            </p>
                        </div>
                        {isEditTrip && vehicle && currentLeg?.id && !dontAllowChangesLegStatuses(currentStatus) ?
                            <SelectTimeOnly
                                name={`legs.${[index]}.arrivedTime`}
                                control={control}
                                placeholder="00:00 AM"
                                allowClearBtn={!watch(`legs.${[index]}.pickupTime`)}
                            />
                            :
                            <TextToolTip
                                classNameText='escort-appointment-patient-main_info-title'
                                textStyles={{fontWeight: 500}}
                                content={watch(`legs.${[index]}.arrivedTime`) ? convertTo12HourFormat(watch(`legs.${[index]}.arrivedTime`)) : ''}
                                text={watch(`legs.${[index]}.arrivedTime`) ? convertTo12HourFormat(watch(`legs.${[index]}.arrivedTime`)) : ''}
                            />
                        }

                    </div>

                    <div className='escort-appointment-patient-main_info-wrapper'
                         style={{
                             maxWidth: 150,
                             pointerEvents: dontAllowChangesLogs ? 'none' : 'auto',
                             opacity: dontAllowChangesLogs ? '.6' : '1'
                         }}
                    >
                        <div className='escort-appointment-patient-main_info-header'>
                            <p className='escort-appointment-patient-main_info-title'>
                                Pickup
                            </p>
                        </div>

                        {isEditTrip && vehicle && currentLeg?.id && !dontAllowChangesLegStatuses(currentStatus) ?
                            <SelectTimeOnly
                                name={`legs.${[index]}.pickupTime`}
                                control={control}
                                placeholder="00:00 AM"
                                minTime={watch(`legs.${[index]}.arrivedTime`)}
                                maxTime={watch(`legs.${[index]}.dropOffTime`)}
                                disabled={!watch(`legs.${[index]}.arrivedTime`)}
                                allowClearBtn={!watch(`legs.${[index]}.dropOffTime`)}
                            />
                            :
                            <TextToolTip
                                classNameText='escort-appointment-patient-main_info-title'
                                textStyles={{fontWeight: 500}}
                                content={watch(`legs.${[index]}.pickupTime`) ? convertTo12HourFormat(watch(`legs.${[index]}.pickupTime`)) : ''}
                                text={watch(`legs.${[index]}.pickupTime`) ? convertTo12HourFormat(watch(`legs.${[index]}.pickupTime`)) : ''}
                            />
                        }
                        {isEditTrip && vehicle && currentLeg?.id && !dontAllowChangesLegStatuses(currentStatus) ?
                            <NumericFormatCustom
                                name={`legs.${[index]}.pickupMileage`}
                                thousandSeparator={true}
                                fixedDecimalScale={true}
                                decimalScale={2}
                                control={control}
                                showError={false}
                                disabled={!watch(`legs.${[index]}.arrivedTime`)}
                            />
                            :
                            <TextToolTip
                                classNameText='escort-appointment-patient-main_info-title'
                                textStyles={{fontWeight: 500}}
                                content={watch(`legs.${[index]}.pickupMileage`) ? watch(`legs.${[index]}.pickupMileage`) + ' mi' : ''}
                                text={watch(`legs.${[index]}.pickupMileage`) ? watch(`legs.${[index]}.pickupMileage`) + ' mi' : ''}
                            />
                        }

                    </div>

                    {/*{watch(`legs.${[index]}.type`) !== LegTypesEnum.ESCORT_PICKUP &&*/}
                    <div
                        className='escort-appointment-patient-main_info-wrapper'
                        style={{
                            maxWidth: 150,
                            pointerEvents: dontAllowChangesLogs ? 'none' : 'auto',
                            opacity: dontAllowChangesLogs ? '.6' : '1'
                        }}
                    >
                        <div className='escort-appointment-patient-main_info-header'>
                            <p className='escort-appointment-patient-main_info-title'>
                                D-Off
                            </p>
                        </div>

                        {isEditTrip && vehicle && currentLeg?.id && !dontAllowChangesLegStatuses(currentStatus) ?
                            <SelectTimeOnly
                                name={`legs.${[index]}.dropOffTime`}
                                control={control}
                                minTime={watch(`legs.${[index]}.pickupTime`)}
                                disabled={!watch(`legs.${[index]}.pickupTime`)}
                                placeholder="00:00 AM"
                            />
                            :
                            <TextToolTip
                                classNameText='escort-appointment-patient-main_info-title'
                                textStyles={{fontWeight: 500}}
                                content={watch(`legs.${[index]}.dropOffTime`) ? convertTo12HourFormat(watch(`legs.${[index]}.dropOffTime`)) : ''}
                                text={watch(`legs.${[index]}.dropOffTime`) ? convertTo12HourFormat(watch(`legs.${[index]}.dropOffTime`)) : ''}
                            />
                        }
                        {isEditTrip && vehicle && currentLeg?.id && !dontAllowChangesLegStatuses(currentStatus) ?
                            <NumericFormatCustom
                                name={`legs.${[index]}.dropOffMileage`}
                                thousandSeparator={true}
                                fixedDecimalScale={true}
                                decimalScale={2}
                                control={control}
                                disabled={!watch(`legs.${[index]}.pickupTime`)}
                                showError={false}
                            />
                            :
                            <TextToolTip
                                classNameText='escort-appointment-patient-main_info-title'
                                textStyles={{fontWeight: 500}}
                                content={watch(`legs.${[index]}.dropOffMileage`) ? watch(`legs.${[index]}.dropOffMileage`) + ' mi' : ''}
                                text={watch(`legs.${[index]}.dropOffMileage`) ? watch(`legs.${[index]}.dropOffMileage`) + ' mi' : ''}
                            />
                        }
                    </div>
                    {/*}*/}
                    <div className='flex' style={{position: 'relative', gap: 12}}>
                        <div
                            className='escort-appointment-patient-main_info-wrapper'
                            style={{
                                border: errors?.legs?.[index]?.pickupAddress?.formattedAddress ? '1px solid #E63946' : '',
                                pointerEvents: ((currentTripId && !isEditTrip) || dontAllowChangesStatuses) ? "none" : '',
                                opacity: ((currentTripId && !isEditTrip) || dontAllowChangesStatuses) ? "0.6" : '',
                            }}
                        >
                            <InputMain
                                className='escort-appointment-patient-main_info-address-input'
                                name={`legs.[${index}].pickupAddress.formattedAddress`}
                                control={control}
                                rules={{
                                    required: true
                                }}
                                allowErrorMessage={false}
                            />

                            <div className='escort-appointment-patient-main_info-header'>
                                <TextToolTip
                                    classNameText='escort-appointment-patient-main_info-title'
                                    content={watch(`legs.${[index]}.pickupAddress.formattedAddress`)}
                                    text={
                                        <p className='escort-appointment-patient-main_info-title'>
                                            Origin: <span
                                            className='escort-appointment-patient-main_info-title'
                                            style={{fontWeight: 500, color: '#172B4D'}}
                                        >
                                                {watch(`legs.${[index]}.pickupAddress.formattedAddress`) || '---'}
                                            </span>
                                        </p>
                                    }
                                    lineClamp={3}
                                />

                                <button
                                    type='button'
                                    className='escort-appointment-patient-main_edit-btn'
                                    onClick={() => changeAddress(`legs.${[index]}.pickupAddress`, `legs.${[index]}.pickupFacility`, 'pickupAddress', 'pickupFacility')}
                                >
                                    <Svg.EditInfoSvg/>
                                </button>
                            </div>
                        </div>

                        <TextToolTip
                            showTooltip
                            arrow
                            placement='top'
                            classNameWrapper='escort-appointment-patient-overwrite-address-btn'
                            text={<Svg.OverwriteSvg/>}
                            content='Switch origin and destination addresses'
                            onClick={overwriteAddresses}
                        />

                        <div
                            className='escort-appointment-patient-main_info-wrapper'
                            style={{
                                border: errors?.legs?.[index]?.dropOffAddress?.formattedAddress ? '1px solid #E63946' : '',
                                pointerEvents: ((currentTripId && !isEditTrip) || dontAllowChangesStatuses) ? "none" : '',
                                opacity: ((currentTripId && !isEditTrip) || dontAllowChangesStatuses) ? "0.6" : ''
                            }}
                        >
                            <InputMain
                                className='escort-appointment-patient-main_info-address-input'
                                name={`legs.${[index]}.dropOffAddress.formattedAddress`}
                                control={control}
                                rules={{
                                    required: true
                                }}
                                allowErrorMessage={false}
                            />

                            <div className='escort-appointment-patient-main_info-header'>
                                <TextToolTip
                                    classNameText='escort-appointment-patient-main_info-title'
                                    content={watch(`legs.${[index]}.dropOffAddress.formattedAddress`)}
                                    text={
                                        <p className='escort-appointment-patient-main_info-title'>
                                            Destination: <span
                                            className='escort-appointment-patient-main_info-title'
                                            style={{fontWeight: 500, color: '#172B4D'}}
                                        >
                                        {watch(`legs.${[index]}.dropOffAddress.formattedAddress`) || '---'}
                                            </span>
                                        </p>
                                    }
                                    lineClamp={3}
                                />

                                <button
                                    type='button'
                                    className='escort-appointment-patient-main_edit-btn'
                                    onClick={() => changeAddress(`legs.${[index]}.dropOffAddress`, `legs.${[index]}.dropOffFacility`, 'dropOffAddress', 'dropOffFacility')}
                                >
                                    <Svg.EditInfoSvg/>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
